import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/site/ProductCard";
import { CATEGORIES as FALLBACK_CATEGORIES } from "@/lib/constants";

const BRAND_DEFAULTS = [
  { id: "apple",   name: "Apple" },
  { id: "samsung", name: "Samsung" },
  { id: "xiaomi",  name: "Xiaomi" },
  { id: "huawei",  name: "HUAWEI" },
  { id: "google",  name: "Google" },
  { id: "honor",   name: "HONOR" },
];

export default function AccessoriesPage() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const model = params.get("model") || "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [brands, setBrands] = useState(BRAND_DEFAULTS);
  const [activeBrand, setActiveBrand] = useState("apple");
  const [modelsByBrand, setModelsByBrand] = useState({});
  const [modelOpen, setModelOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Load categories + brands once
  useEffect(() => {
    api.get("/categories").then((r) => { if (r.data?.length) setCategories(r.data); }).catch(() => {});
    api.get("/catalog/brands").then((r) => { if (r.data?.length) setBrands(r.data); }).catch(() => {});
  }, []);

  // Load models for active brand when picker is open
  useEffect(() => {
    if (!modelOpen || modelsByBrand[activeBrand]) return;
    api.get(`/catalog/models/${activeBrand}`).then((r) => {
      setModelsByBrand((m) => ({ ...m, [activeBrand]: r.data || [] }));
    }).catch(() => {});
  }, [modelOpen, activeBrand, modelsByBrand]);

  // Load products whenever filters change
  useEffect(() => {
    const qs = new URLSearchParams();
    if (cat && cat !== "all") qs.set("category", cat);
    if (model) qs.set("model", model);
    api.get(`/products?${qs.toString()}`).then((r) => setProducts(r.data));
  }, [cat, model]);

  const setCat = (c) => {
    if (c === "all") params.delete("cat");
    else params.set("cat", c);
    setParams(params);
  };
  const setModel = (m) => {
    if (!m) params.delete("model");
    else params.set("model", m);
    setParams(params);
    setModelOpen(false);
  };
  const clearAll = () => {
    params.delete("cat");
    params.delete("model");
    setParams(params);
  };

  const modelsForActiveBrand = modelsByBrand[activeBrand] || [];
  const filteredModels = useMemo(() => {
    if (!search) return modelsForActiveBrand;
    return modelsForActiveBrand.filter((m) => m.toLowerCase().includes(search.toLowerCase()));
  }, [modelsForActiveBrand, search]);

  return (
    <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen" data-testid="accessories-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Zubehör Shop</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-white mt-3 leading-[1.02]">
            Premium Zubehör.<br/>
            <span className="text-zinc-500">Für jedes Modell.</span>
          </h1>
          <p className="text-sm md:text-base text-zinc-500 mt-4 leading-relaxed">
            Passgenau · zuverlässig · langlebig · hochwertig.
          </p>
        </div>

        {/* MODEL FILTER */}
        <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="model-filter-bar">
          <button
            type="button"
            onClick={() => setModelOpen((v) => !v)}
            className={`group inline-flex items-center gap-2 px-4 h-11 rounded-full border text-sm font-medium transition-all ${
              model
                ? "bg-[#DC1928] border-[#DC1928] text-white shadow-lg shadow-[#DC1928]/20"
                : "bg-[#141417] border-white/10 text-zinc-300 hover:border-white/25"
            }`}
            data-testid="model-filter-toggle"
          >
            <Search className="w-3.5 h-3.5" />
            <span>{model ? `Modell: ${model}` : "Modell auswählen"}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${modelOpen ? "rotate-180" : ""}`} />
          </button>

          {(model || cat !== "all") && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-400 hover:text-white hover:bg-white/10"
              data-testid="clear-filters"
            >
              <X className="w-3 h-3" /> Filter zurücksetzen
            </button>
          )}
        </div>

        {/* MODEL PICKER PANEL (collapsible) */}
        {modelOpen && (
          <div className="mt-4 bg-[#141417] border border-white/10 rounded-3xl p-5 md:p-7 fade-up" data-testid="model-picker-panel">
            <div className="flex flex-wrap gap-1.5 mb-4">
              {brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => { setActiveBrand(b.id); setSearch(""); }}
                  className={`px-3.5 h-9 rounded-full text-xs font-medium transition-colors ${
                    activeBrand === b.id
                      ? "bg-white text-[#0A0A0B]"
                      : "bg-[#0A0A0B] text-zinc-300 border border-white/10 hover:border-white/25"
                  }`}
                  data-testid={`model-brand-${b.id}`}
                >
                  {b.name}
                </button>
              ))}
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Modell in ${brands.find((b) => b.id === activeBrand)?.name || "Marke"} suchen…`}
                className="w-full h-10 pl-9 pr-3 rounded-xl bg-[#0A0A0B] border border-white/10 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-[#DC1928]"
                data-testid="model-search-input"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 max-h-[280px] overflow-y-auto pr-1">
              {filteredModels.length === 0 && (
                <div className="col-span-full py-6 text-center text-xs text-zinc-500">Keine Modelle gefunden.</div>
              )}
              {filteredModels.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModel(m)}
                  className={`px-3 py-2 rounded-lg text-xs text-left transition-colors ${
                    m === model
                      ? "bg-[#DC1928] text-white"
                      : "bg-[#0A0A0B] text-zinc-300 border border-white/5 hover:border-white/20 hover:text-white"
                  }`}
                  data-testid={`model-option-${m.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* CATEGORY FILTERS */}
        <div className="mt-6 flex flex-wrap gap-1.5 sm:gap-2" data-testid="category-filters">
          <button
            onClick={() => setCat("all")}
            className={`px-3.5 sm:px-5 h-10 inline-flex items-center rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              cat === "all" ? "bg-white text-[#0A0A0B]" : "bg-[#141417] text-zinc-300 border border-white/5 hover:border-white/15"
            }`}
            data-testid="filter-all"
          >
            Alle
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className={`px-3.5 sm:px-5 h-10 inline-flex items-center rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                cat === c.id ? "bg-white text-[#0A0A0B]" : "bg-[#141417] text-zinc-300 border border-white/5 hover:border-white/15"
              }`}
              data-testid={`filter-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* RESULTS COUNT */}
        <div className="mt-8 text-xs text-zinc-500" data-testid="results-count">
          {products.length} {products.length === 1 ? "Produkt" : "Produkte"}
          {model && <> · kompatibel mit <span className="text-white font-medium">{model}</span></>}
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.sku} p={p} />)}
        </div>

        {products.length === 0 && (
          <div className="mt-20 text-center text-zinc-500 py-16 bg-[#141417] rounded-3xl border border-white/5">
            <Search className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
            <div className="font-display text-base">
              {model
                ? `Keine Zubehöre für ${model} gefunden.`
                : "Keine Produkte in dieser Kategorie."}
            </div>
            {model && (
              <button
                onClick={() => setModel("")}
                className="mt-3 text-xs text-[#FF4F5E] hover:underline"
                data-testid="reset-model"
              >
                Modell-Filter entfernen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
